import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Autocomplete, TextField, Chip, Box, Typography,
  Avatar, CircularProgress
} from '@mui/material';
import validationService from '../../services/validationService';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    height: '45px',
    '& fieldset': {
      borderColor: '#4759694d',
      border: '1px solid #4759694d',
      borderRadius: '4px',
    },
    '&:hover fieldset': {
      borderColor: '#475969',
      borderWidth: '1px',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#475969',
      borderWidth: '1px',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
    padding: '12px 14px',
    color: 'var(--color-blue, #475969)',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '4px',
    fontSize: '12px'
  }
};

const labelSx = {
  mb: 1,
  fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
  fontWeight: 700,
  fontSize: '14px',
  color: '#475969',
  display: 'flex',
  alignItems: 'center',
  gap: 1
};

const ValidatorAssignmentDialog = ({ open, onClose, projectId, taskId, members, onUpdate, currentUser, project, restrictTo }) => {
  const [internalValidators, setInternalValidators] = useState([]);
  const [externalValidators, setExternalValidators] = useState([]);
  const [loading, setLoading] = useState(false);

  const taskName = taskId ? taskId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  const internalMembers = (members || []).filter(m => (m.member_type || 'internal') === 'internal' && m.user_id);
  const externalMembers = (members || []).filter(m => m.member_type === 'external' && m.user_id);

  // Determine what sections the current user can see
  const userId = currentUser?.id || currentUser?._id;
  const createdById = project?.created_by?.id || project?.created_by?._id || project?.created_by;
  const isCreatorOrAdmin = (userId && createdById && String(userId) === String(createdById)) || currentUser?.role === 'admin';
  const currentMember = (members || []).find(m => String(m.user_id) === String(userId));
  const currentMemberType = currentMember?.member_type || 'internal';

  // restrictTo: 'both' = show all, 'internal' = only internal, 'external' = only external, null/undefined = use member type logic
  const showInternalSection = restrictTo === 'both' ? true : restrictTo === 'internal' ? true : restrictTo === 'external' ? false : (isCreatorOrAdmin || currentMemberType === 'internal');
  const showExternalSection = restrictTo === 'both' ? true : restrictTo === 'external' ? true : restrictTo === 'internal' ? false : (isCreatorOrAdmin || currentMemberType === 'external');

  useEffect(() => {
    if (open && projectId && taskId) {
      fetchValidators();
    }
  }, [open, projectId, taskId]);

  const fetchValidators = async () => {
    try {
      const data = await validationService.getTaskValidators(projectId, taskId);
      setInternalValidators(data.internal || []);
      setExternalValidators(data.external || []);
    } catch (err) {
      // Silent fail
    }
  };

  const handleAssign = async (userId, validatorType) => {
    setLoading(true);
    try {
      await validationService.assignValidator(projectId, taskId, { userId, validatorType });
      await fetchValidators();
      if (onUpdate) onUpdate();
    } catch (err) {
      // Error handled by caller
    }
    setLoading(false);
  };

  const handleRemove = async (userId) => {
    setLoading(true);
    try {
      await validationService.removeValidator(projectId, taskId, userId);
      await fetchValidators();
      if (onUpdate) onUpdate();
    } catch (err) {
      // Error handled by caller
    }
    setLoading(false);
  };

  const assignedInternalIds = internalValidators.map(v => v.user_id);
  const assignedExternalIds = externalValidators.map(v => v.user_id);
  const availableInternal = internalMembers.filter(m => !assignedInternalIds.includes(m.user_id));
  const availableExternal = externalMembers.filter(m => !assignedExternalIds.includes(m.user_id));

  const renderOption = (props, option) => (
    <Box component="li" {...props}>
      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
        {option.name?.charAt(0).toUpperCase() || option.email?.charAt(0).toUpperCase()}
      </Avatar>
      <Box>
        <Typography variant="body2">{option.name}</Typography>
        <Typography variant="caption" color="text.secondary">{option.email}</Typography>
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ px: 4 }}>Assign validators — {taskName}</DialogTitle>
      <DialogContent sx={{ px: 4, py: 3 }}>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Internal Validators */}
          {showInternalSection && <Box>
            <Typography variant="body1" sx={labelSx}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#1976d2' }} />
              Internal Validators
            </Typography>
            {internalValidators.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {internalValidators.map(v => (
                  <Chip
                    key={v.user_id}
                    avatar={<Avatar sx={{ width: 22, height: 22, fontSize: 11 }}>{v.name?.charAt(0)}</Avatar>}
                    label={v.name}
                    size="small"
                    onDelete={() => handleRemove(v.user_id)}
                    sx={{
                      bgcolor: '#e3f2fd',
                      color: '#1976d2',
                      border: '1px solid #bbdefb',
                      '& .MuiChip-deleteIcon': { color: '#1976d2', '&:hover': { color: '#0d47a1' } }
                    }}
                  />
                ))}
              </Box>
            )}
            <Autocomplete
              options={availableInternal}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              onChange={(_, value) => {
                if (value) handleAssign(value.user_id, 'internal');
              }}
              value={null}
              loading={loading}
              disabled={loading || availableInternal.length === 0}
              renderOption={renderOption}
              isOptionEqualToValue={(option, value) => option.user_id === value?.user_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type to search internal members..."
                  helperText={
                    internalValidators.length === 0
                      ? 'No internal validators assigned yet'
                      : `${internalValidators.length} internal validator${internalValidators.length > 1 ? 's' : ''} assigned`
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={inputSx}
                />
              )}
            />
          </Box>}

          {/* External Validators */}
          {showExternalSection && <Box>
            <Typography variant="body1" sx={labelSx}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#2e7d32' }} />
              External Validators
            </Typography>
            {externalValidators.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                {externalValidators.map(v => (
                  <Chip
                    key={v.user_id}
                    avatar={<Avatar sx={{ width: 22, height: 22, fontSize: 11 }}>{v.name?.charAt(0)}</Avatar>}
                    label={v.name}
                    size="small"
                    onDelete={() => handleRemove(v.user_id)}
                    sx={{
                      bgcolor: '#e8f5e9',
                      color: '#2e7d32',
                      border: '1px solid #c8e6c9',
                      '& .MuiChip-deleteIcon': { color: '#2e7d32', '&:hover': { color: '#1b5e20' } }
                    }}
                  />
                ))}
              </Box>
            )}
            <Autocomplete
              options={availableExternal}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              onChange={(_, value) => {
                if (value) handleAssign(value.user_id, 'external');
              }}
              value={null}
              loading={loading}
              disabled={loading || availableExternal.length === 0}
              renderOption={renderOption}
              isOptionEqualToValue={(option, value) => option.user_id === value?.user_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type to search external members..."
                  helperText={
                    externalMembers.length === 0
                      ? 'No external members in this project. Add external members in Project Users first.'
                      : externalValidators.length === 0
                        ? 'No external validators assigned yet'
                        : `${externalValidators.length} external validator${externalValidators.length > 1 ? 's' : ''} assigned`
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={inputSx}
                />
              )}
            />
          </Box>}

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 4, py: 2 }}>
        <Button
          onClick={onClose}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            backgroundColor: '#475969',
            '&:hover': { backgroundColor: '#374955' }
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ValidatorAssignmentDialog;
